import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from "@azure/msal-react";
import MainLayout from "components/Layouts/MainLayout";
import AdminCleanupEmptyWorkspacesPage from "pages/AdminCleanupEmptyWorkspacesPage";
import AdminReconcileMissingDeleteRequestsPage from "pages/AdminReconcileMissingDeleteRequestsPage";
import AdminImporterPage from "pages/AdminImporterPage";
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import BackendApiService from "services/BackendApiService";
import { setAccessToken as setInMemoryAccessToken } from "utils/accessTokenStore";
import { isAdmin, userHasRole } from "utils/utils";
import './App.css';
import { msalInstance } from "./authConfig";
import { MaintenanceProvider } from "./contexts/MaintenanceContext";
import AdminPage from "./pages/AdminPage";
import DebugPage from "./pages/DebugPage";
import HomePage from './pages/HomePage';
import ModuleEditPage from "./pages/ModuleEditPage";
import ModulePage from "./pages/ModulePage";
import UnauthorizedPage from "./pages/UnauthorizedPage";
import UserRequestDetailPage from "./pages/UserRequestDetailPage";
import UserRequestsPage from "./pages/UserRequestsPage";
import UserResourcesPage from "./pages/UserResourcesPage";

const AUTH_REDIRECT_IN_PROGRESS_KEY = 'authRedirectInProgress';


function ProtectedComponent() {
  const cleanupToolEnabled = process.env.REACT_APP_ENABLE_ADMIN_CLEANUP_TOOL === 'true';
  const importerToolEnabled = process.env.REACT_APP_ENABLE_ADMIN_MODULE_IMPORTER_TOOL === 'true';
  const reconcileToolEnabled =
    process.env.REACT_APP_ENVIRONMENT === 'local' &&
    process.env.REACT_APP_ENABLE_ADMIN_RECONCILE_MISSING_DELETE_REQUESTS_TOOL === 'true';
  const navigate = useNavigate();
  const [accessToken, setAccessToken] = useState<string>("");
  const [authorized, setAuthorized] = useState<boolean>(true);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isInteractiveSignIn, setIsInteractiveSignIn] = useState<boolean>(() => sessionStorage.getItem(AUTH_REDIRECT_IN_PROGRESS_KEY) === 'true');
  const [showAuthSplash, setShowAuthSplash] = useState<boolean>(false);

  const markInteractiveSignInStart = () => {
    sessionStorage.setItem(AUTH_REDIRECT_IN_PROGRESS_KEY, 'true');
    setIsInteractiveSignIn(true);
  };

  const clearInteractiveSignIn = () => {
    sessionStorage.removeItem(AUTH_REDIRECT_IN_PROGRESS_KEY);
    setIsInteractiveSignIn(false);
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Cleanup legacy token remnants from previous local/session storage based versions.
        localStorage.removeItem('accessToken');
        sessionStorage.removeItem('accessToken');

        const currentUrl = window.location.pathname + window.location.search;
        const storedRequestedUrl = sessionStorage.getItem('requestedUrl');
        const requestedUrl = (storedRequestedUrl && storedRequestedUrl !== '/')
          ? storedRequestedUrl
          : currentUrl;

        await msalInstance.initialize();
        const response = await msalInstance.handleRedirectPromise();
        const account = response?.account || msalInstance.getAllAccounts()[0];

        if (!account) {
          console.log("start new login flow");
          markInteractiveSignInStart();
          if (requestedUrl && requestedUrl !== '/') {
            sessionStorage.setItem('requestedUrl', requestedUrl);
          } else {
            sessionStorage.removeItem('requestedUrl');
          }
          msalInstance.loginRedirect({
            scopes: [`${process.env.REACT_APP_IDENTIFIER_URI}/.default`],
          });
          return;
        } else {
          console.log("already logged in");
        }

        const token = await msalInstance.acquireTokenSilent({
          scopes: [`${process.env.REACT_APP_IDENTIFIER_URI}/.default`],
          account: account,
        });

        clearInteractiveSignIn();
        setAccessToken(token.accessToken);
        setInMemoryAccessToken(token.accessToken);

        const storedUrl = sessionStorage.getItem('requestedUrl');
        if (storedUrl && storedUrl !== '/') {
          sessionStorage.removeItem('requestedUrl');
          navigate(storedUrl, { replace: true });
        }
      } catch (error) {
        console.error("MSAL redirect error", error);
        clearInteractiveSignIn();
        setAccessToken("");
        setInMemoryAccessToken(null);
      } finally {
        setIsInitializing(false);
      }
    };

    initAuth();
  }, [navigate]);

  useEffect(() => {
    const checkIfAuthorized = async (token: string) => {
      const backendApiService = new BackendApiService(token);
      setAuthorized(await backendApiService.authorized());
    };
    if (accessToken) {
      checkIfAuthorized(accessToken);
    }
  }, [accessToken]);

  useEffect(() => {
    const authPending = isInitializing || isInteractiveSignIn;
    if (!authPending) {
      setShowAuthSplash(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowAuthSplash(true);
    }, 450);

    return () => window.clearTimeout(timeoutId);
  }, [isInitializing, isInteractiveSignIn]);

  return (
    <>
      <AuthenticatedTemplate>
        {!isInitializing && accessToken !== "" && (
          authorized ?
            (
              <MaintenanceProvider>
                <Routes>
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/debug" element={isAdmin(accessToken) ? <DebugPage /> : <Navigate to="/unauthorized" />} />
                    <Route path="/module/:platform/:moduleName" element={<ModulePage />} />
                    <Route path="/module/:platform/:moduleName/:id" element={<ModuleEditPage />} />
                    <Route path="/user/requests" element={<UserRequestsPage />} />
                    <Route path="/user/resources" element={<UserResourcesPage />} />
                    <Route path="/user/requests/:id" element={<UserRequestDetailPage />} />
                    <Route path="/admin/modulesconfig" element={isAdmin(accessToken) ? <AdminPage /> : <Navigate to="/unauthorized" />} />
                    <Route path="/admin/importer" element={(isAdmin(accessToken) || userHasRole(accessToken, "IaC.Importer")) && importerToolEnabled ? <AdminImporterPage /> : <Navigate to="/unauthorized" />} />
                    <Route path="/admin/cleanup-empty-workspaces" element={isAdmin(accessToken) && cleanupToolEnabled ? <AdminCleanupEmptyWorkspacesPage /> : <Navigate to="/unauthorized" />} />
                    <Route path="/admin/reconcile-missing-delete-requests" element={isAdmin(accessToken) && reconcileToolEnabled ? <AdminReconcileMissingDeleteRequestsPage /> : <Navigate to="/unauthorized" />} />
                    <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  </Route>
                </Routes>
              </MaintenanceProvider>
            )
            :
            (<UnauthorizedPage />)
        )
        }
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        {showAuthSplash && (
          <div className="login-splash">
            <div className="login-splash-card" role="status" aria-live="polite">
              <div className="login-splash-spinner" aria-hidden="true" />
              <h2>{isInteractiveSignIn ? 'Signing you in' : 'Loading portal'}</h2>
              <p>
                {isInteractiveSignIn
                  ? 'You will be automatically signed in and redirected to the overview page.'
                  : 'Preparing your session.'}
              </p>
            </div>
          </div>
        )}
      </UnauthenticatedTemplate>
    </>
  );
}

function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <ProtectedComponent />
    </MsalProvider>
  );
}

export default App;
